import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ClipboardList, ArrowRight, X } from "lucide-react";

interface IncompleteQuestionnaireDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentStep: number;
  totalSteps: number;
}

export function IncompleteQuestionnaireDialog({
  open,
  onOpenChange,
  currentStep,
  totalSteps,
}: IncompleteQuestionnaireDialogProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleContinue = () => {
    onOpenChange(false);
    navigate("/onboarding/questionnaire");
  };

  const handleDismiss = () => {
    onOpenChange(false);
  };

  const progressPercent = Math.round((currentStep / totalSteps) * 100);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
            <ClipboardList className="h-8 w-8 text-primary" />
          </div>
          <DialogTitle className="text-xl">
            {t("questionnaire.incomplete.title", "Complete Your Survey")}
          </DialogTitle>
          <DialogDescription className="text-center">
            {t(
              "questionnaire.incomplete.description",
              "You're {{percent}}% done! Complete the survey to unlock all member benefits.",
              { percent: progressPercent }
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Progress indicator */}
        <div className="my-4">
          <div className="flex justify-between text-sm text-muted-foreground mb-2">
            <span>{t("questionnaire.incomplete.questionOf", "Question {{current}} of {{total}}", { current: currentStep, total: totalSteps })}</span>
            <span>{t("questionnaire.incomplete.percentComplete", "{{percent}}% complete", { percent: progressPercent })}</span>
          </div>
          <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
            <div 
              className="h-full bg-primary transition-all duration-300"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-col">
          <Button onClick={handleContinue} className="w-full" size="lg">
            {t("questionnaire.incomplete.continue", "Complete Now")}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            onClick={handleDismiss}
            className="w-full text-muted-foreground"
          >
            <X className="mr-2 h-4 w-4" />
            {t("questionnaire.incomplete.later", "Remind Me Later")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
